import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { TooltipService } from '@babybeet/angular-tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { AccessFacadeService } from '../../../../services/facades/access-facade.service';
import { SettingsFacadeService } from '../../../../services/facades/settings-facade.service';
import { DeviceRegisteredIndicatorComponent } from './device-registered-indicator/device-registered-indicator.component';
import { SidebarTransferIndicatorComponent } from './sidebar-transfer-indicator/sidebar-transfer-indicator.component';

@Component({
  selector: 'app-sidebar-status-indicators',
  templateUrl: './sidebar-status-indicators.component.html',
  styleUrls: ['./sidebar-status-indicators.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    DeviceRegisteredIndicatorComponent,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatSidenavModule,
    MatToolbarModule,
    MatTooltipModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    SidebarTransferIndicatorComponent,
  ],
})
export class SidebarStatusIndicatorsComponent implements OnInit, OnDestroy {
  @Output() toggleTooltipEvent = new EventEmitter<{
    event: MouseEvent;
    template: TemplateRef<any>;
  }>();
  @Input() isExpanded: boolean = false;
  @Input() locatorStatus: {
    remaining: number;
    processed: number;
    locatorLog: { locator: string; success: boolean }[];
  } = {
    remaining: 0,
    processed: 0,
    locatorLog: [],
  };

  private subscriptions: Subscription[] = [];
  private tooltipTimeout: number | undefined;
  private demoModeActive: boolean = false;
  private isTooltipOpen: boolean = false;
  private userId: number | undefined;
  private adminMode: boolean = false;

  constructor(
    private accessFacadeService: AccessFacadeService,
    private router: Router,
    private settingsFacadeService: SettingsFacadeService,
    private readonly tooltipService: TooltipService,
  ) {}

  async ngOnInit() {
    this.subscriptions.push(
      this.settingsFacadeService.settings$.subscribe(settings => {
        this.demoModeActive = settings.demoMode;
        this.adminMode = settings.adminMode;
      }),
    );

    this.subscriptions.push(
      this.accessFacadeService.userTokens$.subscribe(userTokens => {
        this.userId = userTokens.token?.related_user_id;
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  toggleTooltip(event: MouseEvent, template: TemplateRef<unknown>): void {
    if (this.demoModeActive) return;

    if (this.isTooltipOpen) {
      this.onHideTooltip();
    } else {
      this.onShowTooltip(event, template);
    }

    this.isTooltipOpen = !this.isTooltipOpen;
  }

  public onDeviceClick() {
    if (!this.adminMode) return;

    this.router.navigateByUrl(`/admin/device-management/user_id/${this.userId}`);
  }

  private onHideTooltip(): void {
    clearTimeout(this.tooltipTimeout);
    this.tooltipService.hide();
  }

  private onShowTooltip(event: MouseEvent, templateRefContent: TemplateRef<any>): void {
    const target = event.target as Element | null;
    if (target) {
      this.tooltipService.show(target, {
        content: templateRefContent,
        context: { $implicit: 'TemplateRef' },
        placement: 'horizontal',
        theme: 'light',
      });
    }
  }
}
